import React from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { epointsApi } from "../services/epointsApi";
import { depositsApi } from "../services/depositsApi";
import {
  ExclamationTriangleIcon,
  ArrowTrendingUpIcon,
  CurrencyEuroIcon,
  ClockIcon,
} from "@heroicons/react/24/outline";
import { useAuth } from "../contexts/AuthContext";
import { useCurrency } from "../contexts/CurrencyContext";
import securityConfig from "../config/security";

const Dashboard: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { dual } = useCurrency();

  // Only check activation status for dealers - use dealer profile endpoint
  const { data: profile } = useQuery({
    queryKey: ["dealer-profile"],
    queryFn: async () => {
      const response = await fetch(
        `${import.meta.env.VITE_API_BASE_URL}/dealer/profile`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem(securityConfig.TOKEN_STORAGE_KEY)}`,
            "Content-Type": "application/json",
          },
        },
      );
      const data = await response.json();
      return data.data; // Extract the nested data
    },
    enabled: user?.role === "dealer",
  });

  const activationStatus = profile
    ? {
        isActivated: profile.activationStatus === "activated",
        activationStatus: profile.activationStatus,
        activationMethod: profile.activationMethod,
        activationDate: profile.activationDate,
        paymentIntentId: profile.activationPaymentId,
      }
    : null;

  // Deposit data is available only for authenticated dealer accounts.
  const {
    data: balance,
    isLoading: balanceLoading,
    error: balanceError,
  } = useQuery({
    queryKey: ["epoint-balance"],
    queryFn: epointsApi.getBalance,
    enabled: user?.role === "dealer",
  });

  const { data: recentTransactions, isLoading: transactionsLoading } = useQuery(
    {
      queryKey: ["recent-transactions"],
      queryFn: () => depositsApi.getTransactions({ page: 1, limit: 5 }),
      enabled: user?.role === "dealer",
    },
  );

  const { data: monthlyStats, isLoading: monthlyStatsLoading } = useQuery({
    queryKey: ["monthly-stats"],
    queryFn: () => depositsApi.getMonthlyStats(),
    enabled: user?.role === "dealer",
  });

  // Show activation warning for dealers who are not activated
  const isDealer = user?.role === "dealer";
  const needsActivation =
    isDealer && activationStatus && !activationStatus.isActivated;

  return (
    <div className="space-y-6">
      {/* Activation Warning for Dealers */}
      {needsActivation && (
        <div className="bg-yellow-50/20 border border-yellow-200 rounded-lg p-6">
          <div className="flex">
            <div className="flex-shrink-0">
              <ExclamationTriangleIcon className="h-5 w-5 text-yellow-400" />
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-yellow-800">
                {t("activation.required")}
              </h3>
              <div className="mt-2 text-sm text-yellow-700">
                <p>{t("activation.description")}</p>
              </div>
              <div className="mt-4">
                <button
                  onClick={() => navigate("/dealer/activation")}
                  className="bg-yellow-100 text-yellow-800 px-4 py-2 rounded-md text-sm font-medium hover:bg-yellow-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-yellow-500"
                >
                  {t("activation.activateButton")}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Welcome Section */}
      <div className="bg-gradient-to-r from-primary-600 to-primary-800 rounded-lg shadow p-6 text-white">
        <h1 className="text-2xl font-bold mb-2">
          {t("dashboard.welcome", { name: user?.name || "User" })}
        </h1>
        <p className="text-primary-100">
          {isDealer
            ? t("dashboard.dealerDescription")
            : t("dashboard.customerDescription")}
        </p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* Balance Overview - For dealers only */}
        {user?.role === "dealer" && (
          <div className="md:col-span-2">
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-lg font-semibold text-secondary mb-4">
                {t("dashboard.balanceOverview")}
              </h3>
              {balanceLoading ? (
                <div className="animate-pulse">
                  <div className="h-8 bg-gray-200 rounded w-32 mb-2"></div>
                  <div className="h-4 bg-gray-200 rounded w-24"></div>
                </div>
              ) : balanceError ? (
                <div className="text-center py-4">
                  <p className="text-red-500 text-sm">
                    {t("dashboard.errorLoadingBalance")}
                  </p>
                  <p className="text-gray-400 text-xs">
                    {balanceError.message}
                  </p>
                </div>
              ) : (
                <div>
                  <div className="text-3xl font-bold text-secondary mb-2">
                    {t("dashboard.availableBalance", {
                      points: balance?.availablePoints?.toLocaleString() || 0,
                    })}
                  </div>
                  <p className="text-sm text-gray-500">
                    {t("dashboard.availableCash", {
                      amount: dual(balance?.availableCashValue || 0),
                    })}
                  </p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Dealer Status - Only for dealers */}
        {user?.role === "dealer" && (
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-secondary">
                {t("dashboard.dealerStatus")}
              </h3>
              <div
                className={`p-2 rounded-lg ${
                  profile?.activationStatus === "activated"
                    ? "bg-green-100"
                    : "bg-yellow-100"
                }`}
              >
                <div
                  className={`h-5 w-5 ${
                    profile?.activationStatus === "activated"
                      ? "text-primary-600"
                      : "text-yellow-600"
                  }`}
                >
                  {profile?.activationStatus === "activated" ? "✓" : "⏳"}
                </div>
              </div>
            </div>
            <div className="text-2xl font-bold text-secondary mb-2">
              {t("dashboard.accountStatus", {
                status:
                  profile?.activationStatus === "activated"
                    ? t("dashboard.activated")
                    : t("dashboard.pending"),
              })}
            </div>
            <p className="text-sm text-gray-500">
              {profile?.activationStatus === "activated"
                ? t("dashboard.activatedDescription")
                : t("dashboard.pendingDescription")}
            </p>
          </div>
        )}

        {/* Quick Stats - Only for dealers */}
        {user?.role === "dealer" && (
          <div className="space-y-4">
            <div className="bg-white rounded-lg shadow p-4">
              <div className="flex items-center">
                <ArrowTrendingUpIcon
                  className={`h-8 w-8 ${
                    monthlyStatsLoading
                      ? "text-gray-400"
                      : (monthlyStats?.growth?.amountGrowthPercent || 0) >= 0
                        ? "text-green-500"
                        : "text-red-500"
                  }`}
                />
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">
                    {t("dashboard.thisMonth")}
                  </p>
                  {monthlyStatsLoading ? (
                    <div className="animate-pulse">
                      <div className="h-8 bg-gray-200 rounded w-20"></div>
                    </div>
                  ) : (
                    <div>
                      <p className="text-2xl font-semibold text-secondary">
                        {monthlyStats?.currentMonth?.totalAmount?.toFixed(2) ||
                          "0.00"}
                        €
                      </p>
                      {monthlyStats &&
                        monthlyStats.growth &&
                        monthlyStats.growth.amountGrowthPercent !== 0 && (
                          <p
                            className={`text-sm ${
                              monthlyStats.growth.amountGrowthPercent >= 0
                                ? "text-primary-600"
                                : "text-red-600"
                            }`}
                          >
                            {monthlyStats.growth.amountGrowthPercent >= 0
                              ? "+"
                              : ""}
                            {monthlyStats.growth.amountGrowthPercent}% vs last
                            month
                          </p>
                        )}
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow p-4">
              <div className="flex items-center">
                <CurrencyEuroIcon className="h-8 w-8 text-primary-500" />
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">
                    {t("dashboard.exchangeRate")}
                  </p>
                  <p className="text-lg font-semibold text-secondary">
                    1€ = 100 ePoints
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Recent Transactions - Only for dealers */}
      {user?.role === "dealer" && (
        <div className="bg-white rounded-lg shadow">
          <div className="px-6 py-4 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold text-secondary">
                {t("dashboard.recentTransactions")}
              </h2>
              <ClockIcon className="h-5 w-5 text-gray-400" />
            </div>
          </div>

          <div className="p-6">
            {transactionsLoading ? (
              <div className="space-y-4">
                {[...Array(3)].map((_, i) => (
                  <div
                    key={i}
                    className="animate-pulse flex items-center space-x-4"
                  >
                    <div className="h-10 w-10 bg-gray-200 rounded-full"></div>
                    <div className="flex-1 space-y-2">
                      <div className="h-4 bg-gray-200 rounded w-1/4"></div>
                      <div className="h-3 bg-gray-200 rounded w-1/2"></div>
                    </div>
                    <div className="h-4 bg-gray-200 rounded w-20"></div>
                  </div>
                ))}
              </div>
            ) : recentTransactions?.deposits?.length ? (
              <div className="space-y-4">
                {recentTransactions.deposits.map((transaction: any) => (
                  <div
                    key={transaction.id}
                    className="flex items-center justify-between p-4 bg-gray-50 rounded-lg"
                  >
                    <div className="flex items-center space-x-4">
                      <div
                        className={`h-10 w-10 rounded-full flex items-center justify-center ${
                          transaction.status === "completed"
                            ? "bg-green-100 text-primary-600"
                            : transaction.status === "pending"
                              ? "bg-yellow-100 text-yellow-600"
                              : transaction.status === "processing"
                                ? "bg-primary-100 text-primary-600"
                                : transaction.status === "cancelled"
                                  ? "bg-gray-100 text-gray-600"
                                  : "bg-red-100 text-red-600"
                        }`}
                      >
                        <CurrencyEuroIcon className="h-5 w-5" />
                      </div>
                      <div>
                        <p className="font-medium text-secondary">
                          {t("transaction.deposit")}
                        </p>
                        <p className="text-sm text-gray-500">
                          {new Date(transaction.createdAt).toLocaleDateString()}
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="font-semibold text-secondary">
                        +{transaction.epointsAwarded.toLocaleString()} ePoints
                      </p>
                      <p className="text-sm text-gray-500">
                        €{transaction.amount.toFixed(2)}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <CurrencyEuroIcon className="mx-auto h-12 w-12 text-gray-400" />
                <h3 className="mt-2 text-sm font-medium text-secondary">
                  {t("dashboard.noTransactions")}
                </h3>
                <p className="mt-1 text-sm text-gray-500">
                  {t("dashboard.makeFirstDeposit")}
                </p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Customer Warning - Only for customers */}
      {user?.role === "customer" && (
        <div className="bg-red-50/20 border border-red-200 rounded-lg p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <svg
                className="h-5 w-5 text-red-400"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fillRule="evenodd"
                  d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                  clipRule="evenodd"
                />
              </svg>
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">
                {t("customer.accessRestricted")}
              </h3>
              <div className="mt-2 text-sm text-red-700">
                <p>{t("customer.restrictedDescription")}</p>
                <p className="mt-1">{t("customer.contactSupport")}</p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Dashboard;
